<template>
  <div class="tm-kalender">
    <div class="tm-kalender__kopf">
      <button type="button" class="tm-day-nav__btn" @click="vorherigerMonat" aria-label="Vorheriger Monat">‹</button>
      <span class="tm-kalender__monat">{{ monatLabel }}</span>
      <button type="button" class="tm-day-nav__btn" @click="naechsterMonat" aria-label="Nächster Monat">›</button>
    </div>

    <div class="tm-kalender__wochentage">
      <span v-for="wt in wochentage" :key="wt">{{ wt }}</span>
    </div>

    <div class="tm-kalender__tage">
      <button
        v-for="tag in alleTageDerAnsicht"
        :key="tag.schluessel"
        type="button"
        class="tm-kalender__tag"
        :class="{
          'tm-kalender__tag--spiel': tag.gruppen.length > 0,
          'tm-kalender__tag--aktiv': tag.schluessel === ausgewaehlterTag,
          'tm-kalender__tag--heute': tag.istHeute,
          'tm-kalender__tag--anderer-monat': !tag.istAktuellerMonat
        }"
        :disabled="tag.gruppen.length === 0"
        @click="$emit('select-day', tag.schluessel)"
      >
        <span class="tm-kalender__tagnummer">{{ tag.tag }}</span>

        <span v-if="tag.gruppen.length > 0 && tag.gruppen.length <= props.punkteAbAnzahl" class="tm-kalender__badges">
          <span v-for="g in tag.gruppen.slice(0, props.maxBadges)" :key="g.kurz" class="tm-kalender__badge">
            {{ g.kurz }}&nbsp;{{ g.anzahl }}
          </span>
          <span v-if="tag.gruppen.length > props.maxBadges" class="tm-kalender__badge tm-kalender__badge--mehr">
            +{{ tag.gruppen.length - props.maxBadges }}
          </span>
        </span>

        <span v-else-if="tag.gruppen.length > 0" class="tm-kalender__punkte">
          <span
            v-for="g in tag.gruppen"
            :key="g.kurz"
            :class="['tm-kalender__punkt', `tm-kalender__punkt--${ligaKategorie(g.ligaId)}`]"
            :title="`${g.kurz} (${g.anzahl})`"
          ></span>
        </span>
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ligaKategorie } from '../lib/ligaTypen.js'

const props = defineProps({
  verfuegbareTage: { type: Array, required: true }, // ['YYYY-MM-DD', ...]
  spieltageGruppen: { type: Object, default: () => ({}) }, // { 'YYYY-MM-DD': [{kurz, anzahl, ligaId}, ...] }
  ausgewaehlterTag: { type: String, default: null },
  maxBadges: { type: Number, default: 1 }, // wie viele Liga-Badges pro Tag, bevor '+N' zusammenfasst (nur in der Text-Ansicht)
  // Ab wie vielen Ligen an einem Tag auf die kompakten Kategorie-Punkte umgeschaltet wird, statt
  // Text-Badges zu zeigen (die sonst die Zellenhöhe sprengen würden, siehe oben).
  punkteAbAnzahl: { type: Number, default: 3 }
})
defineEmits(['select-day'])

const wochentage = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

// Start-Monat: der Monat des aktuell gewählten Tags, sonst der heutige Monat
const anfangsDatum = props.ausgewaehlterTag ? new Date(props.ausgewaehlterTag) : new Date()
const angezeigterMonat = ref(new Date(anfangsDatum.getFullYear(), anfangsDatum.getMonth(), 1))

function vorherigerMonat() {
  angezeigterMonat.value = new Date(angezeigterMonat.value.getFullYear(), angezeigterMonat.value.getMonth() - 1, 1)
}
function naechsterMonat() {
  angezeigterMonat.value = new Date(angezeigterMonat.value.getFullYear(), angezeigterMonat.value.getMonth() + 1, 1)
}

const monatLabel = computed(() =>
  angezeigterMonat.value.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
)

// Montag = 0 ... Sonntag = 6 (statt JS-Standard Sonntag = 0)
function montagsIndex(datum) {
  return (datum.getDay() + 6) % 7
}

const heuteSchluessel = (() => {
  const h = new Date()
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`
})()

// tageImMonat bekommt zusätzlich istAktuellerMonat: true
const tageImMonat = computed(() => {
  const jahr = angezeigterMonat.value.getFullYear()
  const monat = angezeigterMonat.value.getMonth()
  const anzahlTage = new Date(jahr, monat + 1, 0).getDate()

  const ergebnis = []
  for (let tag = 1; tag <= anzahlTage; tag++) {
    const schluessel = `${jahr}-${String(monat + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`
    ergebnis.push({
      tag,
      schluessel,
      gruppen: props.spieltageGruppen[schluessel] ?? [],
      istHeute: schluessel === heuteSchluessel,
      istAktuellerMonat: true
    })
  }
  return ergebnis
})

// Rand-Tage des VORMONATS, um die erste Woche aufzufüllen (Anzahl = fuehrendeLeerzellen von vorhin)
const fuehrendeTage = computed(() => {
  const jahr = angezeigterMonat.value.getFullYear()
  const monat = angezeigterMonat.value.getMonth()
  const anzahl = montagsIndex(angezeigterMonat.value)
  if (anzahl === 0) return []

  const vormonatLetzterTag = new Date(jahr, monat, 0).getDate()
  const vormonatJahr = monat === 0 ? jahr - 1 : jahr
  const vormonatMonat0 = monat === 0 ? 11 : monat - 1

  const ergebnis = []
  for (let i = anzahl - 1; i >= 0; i--) {
    const tag = vormonatLetzterTag - i
    const schluessel = `${vormonatJahr}-${String(vormonatMonat0 + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`
    ergebnis.push({
      tag,
      schluessel,
      gruppen: props.spieltageGruppen[schluessel] ?? [],
      istHeute: schluessel === heuteSchluessel,
      istAktuellerMonat: false
    })
  }
  return ergebnis
})

// Rand-Tage des FOLGEMONATS, um die letzte Woche auf volle 7 Spalten aufzufüllen
const nachfolgendeTage = computed(() => {
  const belegt = fuehrendeTage.value.length + tageImMonat.value.length
  const anzahl = (7 - (belegt % 7)) % 7
  if (anzahl === 0) return []

  const jahr = angezeigterMonat.value.getFullYear()
  const monat = angezeigterMonat.value.getMonth()
  const folgemonatJahr = monat === 11 ? jahr + 1 : jahr
  const folgemonatMonat0 = monat === 11 ? 0 : monat + 1

  const ergebnis = []
  for (let tag = 1; tag <= anzahl; tag++) {
    const schluessel = `${folgemonatJahr}-${String(folgemonatMonat0 + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`
    ergebnis.push({
      tag,
      schluessel,
      gruppen: props.spieltageGruppen[schluessel] ?? [],
      istHeute: schluessel === heuteSchluessel,
      istAktuellerMonat: false
    })
  }
  return ergebnis
})

const alleTageDerAnsicht = computed(() => [
  ...fuehrendeTage.value,
  ...tageImMonat.value,
  ...nachfolgendeTage.value
])
</script>